import { TREE_KEYBOARD_SHORTCUTS, treeShortcutHint } from '@/lib/tree/treeKeyboardShortcuts';
import { NodeType } from '@/types/tree';
import { ContextMenuAction } from '@/types/tree/actions';

/**
 * Tooltip message can be either a static string or a function that generates a string
 */
type TooltipMessage = string | ((nodeType: NodeType) => string);

/**
 * Tooltip configuration for menu items
 */
export interface MenuItemTooltip {
  /** Tooltip text when the action is available */
  enabled: TooltipMessage;
  /** Tooltip text when the action is disabled */
  disabled: TooltipMessage;
  /** Tooltip text when the action is loading */
  loading: string;
}

/**
 * Tooltip provider for generating consistent tooltips
 */
export class TooltipProvider {
  private static readonly tooltips: Record<ContextMenuAction, MenuItemTooltip> = {
    reload: {
      enabled: (nodeType: NodeType) =>
        `Refresh this ${nodeType}'s data from the server${treeShortcutHint(TREE_KEYBOARD_SHORTCUTS.reload)}`,
      disabled: (nodeType: NodeType) => `Cannot refresh ${nodeType} data`,
      loading: 'Refreshing data...',
    },
    'add-processing-activity': {
      enabled: (nodeType: NodeType) => `Add a new processing activity to this ${nodeType}`,
      disabled: (nodeType: NodeType) => `Cannot add processing activity to this ${nodeType}`,
      loading: 'Creating activity...',
    },
    'add-char-activity': {
      enabled: () => `Characterize this samples`,
      disabled: (nodeType: NodeType) => `Cannot add characterization activity to this ${nodeType}`,
      loading: 'Creating activity...',
    },
    'add-in-situ-char-activity': {
      enabled: () => `Add in-situ characterization to this activity`,
      disabled: () => `Cannot add in-situ characterization to this activity`,
      loading: 'Creating activity...',
    },
    'add-sample': {
      enabled: () => 'Add a new sample to this project',
      disabled: () => 'Cannot add sample to this project',
      loading: 'Creating sample...',
    },
    'add-theory-activity': {
      enabled: () => 'Add a new theory activity to this data package',
      disabled: () => 'Cannot add theory activity to this data package',
      loading: 'Creating theory...',
    },
    'add-proposal': {
      enabled: () => 'Add a new proposal to this project',
      disabled: () => 'Cannot add proposals to this project',
      loading: 'Creating proposal...',
    },
    'add-decision': {
      enabled: () => 'Add a new decision to this project',
      disabled: () => 'Cannot add decisions to this project',
      loading: 'Creating decision...',
    },
    'add-project-document': {
      enabled: () => 'Add a new project document to this project',
      disabled: () => 'Cannot add project documents to this project',
      loading: 'Creating document...',
    },
    'add-report': {
      enabled: () => 'Add a new report to this project',
      disabled: () => 'Cannot add reports to this project',
      loading: 'Creating report...',
    },
    'add-publication': {
      enabled: () => 'Add a new publication to this project',
      disabled: () => 'Cannot add publications to this project',
      loading: 'Creating publication...',
    },
    'add-data-package': {
      enabled: () => 'Add a new data package to this project',
      disabled: () => 'Cannot add data packages to this project',
      loading: 'Creating data package...',
    },
    'add-communication': {
      enabled: () => 'Add a new communication to this project',
      disabled: () => 'Cannot add communications to this project',
      loading: 'Creating communication...',
    },
    duplicate: {
      enabled: (nodeType: NodeType) => `Create a copy of this ${nodeType}`,
      disabled: (nodeType: NodeType) => `Cannot duplicate this ${nodeType}`,
      loading: 'Creating copy...',
    },
    delete: {
      enabled: (nodeType: NodeType) => `Delete this ${nodeType}`,
      disabled: (nodeType: NodeType) => `Cannot delete this ${nodeType}`,
      loading: 'Deleting...',
    },
    share: {
      enabled: (nodeType: NodeType) => `Share this ${nodeType} with others`,
      disabled: (nodeType: NodeType) => `Cannot share this ${nodeType}`,
      loading: 'Opening share dialog...',
    },
    'view-data': {
      enabled: (nodeType: NodeType) => `View data for this ${nodeType}`,
      disabled: (nodeType: NodeType) => `No data available for this ${nodeType}`,
      loading: 'Loading data...',
    },
    download: {
      enabled: 'Create a SharePoint folder with all data',
      disabled: (nodeType: NodeType) => `Cannot create SharePoint folder for this ${nodeType}`,
      loading: 'Creating SharePoint folder...',
    },
    split: {
      enabled: () => 'Split this sample into multiple pieces',
      disabled: () => 'Cannot split this sample',
      loading: 'Splitting sample...',
    },
    transfer: {
      enabled: () => 'Transfer this sample to other projects',
      disabled: () => 'Cannot transfer this sample',
      loading: 'Transferring sample...',
    },
    ship: {
      enabled: () => 'Ship this sample to another location',
      disabled: () => 'Cannot ship this sample',
      loading: 'Shipping sample...',
    },
    'add-storage': {
      enabled: () => 'Add storage information to this sample',
      disabled: () => 'Cannot add storage information to this sample',
      loading: 'Adding storage information...',
    },
    submit: {
      enabled: () => 'Submit data, making it read-only',
      disabled: (nodeType: NodeType) => `Cannot submit this ${nodeType}`,
      loading: 'Submitting sample...',
    },
    reset: {
      enabled: () => `Reset the status to pending, making it editable again`,
      disabled: (nodeType: NodeType) => `Cannot reset this ${nodeType}`,
      loading: 'Resetting sample...',
    },
    'remove-from-data-package': {
      enabled: () => 'Remove this item from the data package',
      disabled: () => 'Cannot remove this item from the data package',
      loading: 'Removing from data package...',
    },
  };

  /**
   * Generates a tooltip for a menu item based on its action and state
   * @param action - The action identifier
   * @param nodeType - The type of node the action is being performed on
   * @param isEnabled - Whether the action is currently enabled
   * @param isLoading - Whether the action is currently loading
   * @returns The appropriate tooltip text
   */
  static getTooltip(action: ContextMenuAction, nodeType: NodeType, isEnabled: boolean, isLoading: boolean): string {
    const tooltip = this.tooltips[action];
    if (!tooltip) return '';

    if (isLoading) return tooltip.loading;

    const message = isEnabled ? tooltip.enabled : tooltip.disabled;
    return typeof message === 'function' ? message(nodeType) : message;
  }

  /**
   * Adds or updates a tooltip configuration
   * @param action - The action identifier
   * @param tooltip - The tooltip configuration
   */
  static setTooltip(action: ContextMenuAction, tooltip: MenuItemTooltip): void {
    this.tooltips[action] = tooltip;
  }
}
